import type { Locale } from 'vue-i18n'
import { useSessionStorage } from '@vueuse/core'
import { SITE_ID } from '@/constants/storageKey'

export function useExternalLinks() {
  const config = useRuntimeConfig()
  const i18nInstance = useGlobalI18n()
  const abbrLocale = computed(() => (i18nInstance.localeProperties.value?.['iso-abbrev'] as string | undefined) || 'en')

  const abbrLocalePathMap: Record<string, string> = {
    zh: '/membership-zh',
    cn: '/elite-en',
    ja: '/membership-zh',
    en: '/elite-en',
    th: '/membership-th',
  }

  const membershipLocalePathMap: Record<string, string> = {
    zh: '/membership-zh',
    th: '/membership-th',
  }

  const whatsEliteLink = computed(() => `${config.public.EVENT_WEBSITE_URL}${abbrLocalePathMap[abbrLocale.value]}`)
  const membershipLink = computed(() => `${config.public.EVENT_WEBSITE_URL}${membershipLocalePathMap[abbrLocale.value] ?? '/membership-en'}`)
  const whyFunNowLink = computed(() => `${config.public.EVENT_WEBSITE_URL}/whyfunnow-${abbrLocale.value}`)
  const brandStoryLink = computed(() => `${config.public.EVENT_WEBSITE_URL}/brandstory-${abbrLocale.value}`)

  return {
    whatsEliteLink,
    membershipLink,
    whyFunNowLink,
    brandStoryLink,
  }
}

export function useDownloadLinks() {
  const config = useRuntimeConfig()
  const nuxtApp = useNuxtApp()
  const route = useRoute()
  const siteIdSession = useSessionStorage<string>(SITE_ID, '')

  const abbrLocale = computed(() => (nuxtApp.$i18n.localeProperties.value['iso-abbrev'] as string | undefined) || 'en')
  const allowedLocale = computed(() => (abbrLocale.value === 'th' ? 'en' : abbrLocale.value))
  // TODO: some URL cases are not handled yet(e.g., subcategory page URL)
  const destination = computed(() => `${config.public.BASE_URL}${route.path}`)

  const constructLink = (baseLink: string, extraParams: Record<string, string> = {}) => {
    if (!baseLink) {
      return ''
    }

    try {
      const url = new URL(baseLink)
      url.searchParams.set('destination', destination.value)

      for (const [key, value] of Object.entries(extraParams)) {
        url.searchParams.set(key, value)
      }

      return url.toString()
    } catch {
      return baseLink
    }
  }

  const iosLink = computed(() => constructLink(config.public.DOWNLOAD_LINK.IOS))
  const androidLink = computed(() => constructLink(config.public.DOWNLOAD_LINK.ANDROID))
  const unifiedLink = computed(() =>
    constructLink(config.public.DOWNLOAD_LINK.UNIFIED, { site_id: siteIdSession.value, locale: allowedLocale.value })
  )

  return {
    iosLink,
    androidLink,
    unifiedLink,
  }
}

export function usePartnerLinks() {
  const config = useRuntimeConfig()
  const { locale } = useGlobalI18n()

  const PARTNER_LINK: Record<Locale, string> = {
    'zh-tw': `${config.public.EVENT_WEBSITE_URL}/joinus-zh`,
    'zh-cn': `${config.public.EVENT_WEBSITE_URL}/joinus-zh`,
    ja: 'https://funnow.typeform.com/to/LEkks6',
    en: `${config.public.EVENT_WEBSITE_URL}/joinus-en`,
    th: `${config.public.EVENT_WEBSITE_URL}/joinus-en`,
  }

  const SASS_PARTNER_LINK: Record<Locale, string> = {
    'zh-tw': `${config.public.EVENT_WEBSITE_URL}/booking-zh`,
    'zh-cn': `${config.public.EVENT_WEBSITE_URL}/booking-zh`,
    ja: `${config.public.EVENT_WEBSITE_URL}/booking-en`,
    en: `${config.public.EVENT_WEBSITE_URL}/booking-en`,
    th: `${config.public.EVENT_WEBSITE_URL}/booking-en`,
  }

  const partnerLink = computed(() => PARTNER_LINK[locale.value])
  const sassPartnerLink = computed(() => SASS_PARTNER_LINK[locale.value])

  return {
    partnerLink,
    sassPartnerLink,
  }
}
